/* eslint-disable @typescript-eslint/no-explicit-any */
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { z } from 'zod';

type FormControls<T> = { [K in keyof T]-?: FormControl<T[K] | undefined> };

export class CustomFormGroup<T extends z.ZodType<any, any, any>> extends FormGroup<FormControls<z.input<T>>> {
  constructor(
    controls: FormControls<z.input<T>>,
    public schema: T,
  ) {
    super(controls);
  }

  _forEachChild(cb: (v: any, k: any) => void): void {
    Object.keys(this.controls).forEach((key) => {
      const control = (this.controls as any)[key];
      control && cb(control, key);
    });
  }

  _reduceChildren<T, K extends keyof FormControls<T>>(
    initValue: T,
    fn: (acc: T, control: FormControls<T>[K], name: K) => T,
  ): T {
    let res = initValue;
    this._forEachChild((control: FormControls<T>[K], name: K) => {
      res = fn(res, control, name);
    });
    return res;
  }

  override getRawValue() {
    return this._reduceChildren({}, (acc, control, name) => {
      const value = (control as any).getRawValue();

      if (value != null && !this.isEmptyObject(value)) (acc as any)[name] = value;

      return acc;
    }) as any;
  }

  getTypedValue() {
    return this.schema.parse(this.getRawValue()) as z.infer<T>;
  }

  isEmptyObject(obj: object) {
    return Object.keys(obj).length === 0 && obj.constructor === Object;
  }

  patchTypedValue(value: z.infer<T>) {
    this.patchValue(value);
  }

  getUpdateValues(obj: any, customSchema?: z.ZodType<any, any, any>): Record<any, any> {
    const updates = this.getUpdatedFields(obj);
    const result = updates.reduce((acc: any, { key, newValue }) => {
      acc[key] = newValue;
      acc['id'] = obj['id'];
      return acc;
    }, {});
    return (customSchema ?? this.schema).parse(result);
  }

  getUpdatedFields(obj: any): { key: string; oldValue: any; newValue: any }[] {
    return this._getObjectDifferences(this.getRawValue(), obj);
  }

  _getObjectDifferences(obj1: any, obj2: any, parentKey = ''): { key: string; oldValue: any; newValue: any }[] {
    const differences: { key: string; oldValue: any; newValue: any }[] = [];
    const compareObjects = (obj1: any, obj2: any, parentKey = '') => {
      for (const key in obj1) {
        if (Object.hasOwn(obj1, key)) {
          const currentKey = parentKey ? `${parentKey}.${key}` : key;
          if (Array.isArray(obj1[key]) && Array.isArray(obj2[key])) {
            if (obj1[key].length !== obj2[key].length) {
              differences.push({
                key: key,
                oldValue: obj2[key],
                newValue: obj1[key],
              });
            } else {
              for (let i = 0; i < obj1[key].length; i++) {
                if (obj1[key][i] !== obj2[key][i]) {
                  differences.push({
                    key: key,
                    oldValue: obj2[key],
                    newValue: obj1[key],
                  });
                  break;
                }
              }
            }
          } else if (typeof obj1[key] === 'object' && typeof obj2[key] === 'object') {
            compareObjects(obj1[key], obj2[key], currentKey);
          } else if (obj1[key] !== obj2[key]) {
            differences.push({
              key: currentKey,
              oldValue: obj2[key],
              newValue: obj1[key],
            });
          }
        }
      }
    };

    compareObjects(obj1, obj2, parentKey);

    return differences;
  }
}

export function objectValidatorOptional(scheme: z.ZodTypeAny) {
  return objectValidator(scheme, true);
}

export function objectValidator(scheme: z.ZodTypeAny, optional?: boolean) {
  return (control: AbstractControl) => {
    const value = control.value;
    if (optional && value == null) return null;
    if (value == null) return { object: 'invalid-object' };
    const result = scheme.safeParse(value);
    if (result.success) return null;
    return { object: 'invalid-object' };
  };
}

export function requiredWithEmptyArray(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value == null) return { required: true };
    if (!Array.isArray(value)) return { object: 'invalid-object' };
    return null;
  };
}
